import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardAppShell from '../components/DashboardAppShell';
import DataPrivacyPromise from '../components/DataPrivacyPromise';
import { useApp } from '../context/AppContext';
import { LOSS_TYPES } from '../data/models';

const FONT_SERIF = "'Playfair Display', Georgia, serif";
const FONT_BODY = "'Plus Jakarta Sans', -apple-system, sans-serif";

const CHAPTER_CONFIG = {
  'first-chapter': { label: 'Trying to Conceive', emoji: '💜', color: '#7F77DD' },
  ttc: { label: 'Trying to Conceive', emoji: '💜', color: '#D9734F' },
  fertility: { label: 'Fertility', emoji: '🌸', color: '#D4537E' },
  pregnancy: { label: 'Pregnancy', emoji: '🤰', color: '#14B8A6' },
  postpartum: {
    label: 'Postpartum & Beyond',
    emoji: '🤱',
    color: '#9B2942',
    colorLight: '#F5EAED',
    colorMid: '#C9687E',
    colorDark: '#6B1C2E',
    bg: '#F5EAED',
  },
  menopause: {
    label: 'Motherhood',
    emoji: '🤱',
    color: '#9B2942',
    colorLight: '#F5EAED',
    colorMid: '#C9687E',
    colorDark: '#6B1C2E',
    bg: '#F5EAED',
  },
  cycle: { label: 'Trying to Conceive', emoji: '💜', color: '#7F77DD' },
  ivf: { label: 'Fertility', emoji: '🌸', color: '#D4537E' },
};

function SectionCard({ title, children, onEdit, onExport }) {
  const [editing, setEditing] = useState(false);
  return (
    <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontFamily: FONT_SERIF, fontSize: 18, color: 'var(--text)' }}>{title}</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={() => setEditing(!editing)} style={btnSecondary}>Edit</button>
          {onExport && <button type="button" onClick={onExport} style={btnSecondary}>Share with my doctor</button>}
        </div>
      </div>
      {children({ editing })}
      <DataPrivacyPromise />
    </div>
  );
}

const btnPrimary = { padding: '10px 20px', background: 'var(--pink)', color: 'white', border: 'none', borderRadius: 999, cursor: 'pointer', fontSize: 14 };
const btnSecondary = { padding: '8px 14px', background: 'var(--sand)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 999, cursor: 'pointer', fontSize: 13 };

function exportSection(sectionTitle, content) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `HealthRecord_${sectionTitle.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function HealthRecord() {
  const {
    user,
    journey,
    getJourneyTimeline,
    getHealthRecord,
    updateHealthRecord,
    getLossHistory,
    setLossHistory,
  } = useApp();

  const timeline = getJourneyTimeline(user);
  const hr = getHealthRecord(user);
  const lossHistory = getLossHistory?.(user) ?? [];

  const chapter = (user?.journeyType || timeline?.chapter || 'fertility').toLowerCase();
  const chapterKey = chapter === 'ivf' ? 'fertility' : chapter === 'cycle' ? 'first-chapter' : chapter;
  const chapterInfo = CHAPTER_CONFIG[chapterKey] || CHAPTER_CONFIG.fertility;
  const chapterLabel = chapterInfo?.label || 'Your chapter';
  const hasLoss = Boolean(user?.has_loss);

  const journeyOverview = hr.journeyOverview || {};
  const fertility = hr.fertility || {};
  const pregnancy = hr.pregnancy || {};
  const ttc = hr.ttc || {};
  const postpartum = hr.postpartum || {};
  const menopause = hr.menopause || {};

  const ivfCycles = fertility.ivfCycles || [];
  const labResults = fertility.labResults || [
    { name: 'AMH', value: '', date: '', trend: '', normalRange: '1.0–4.0 ng/mL' },
    { name: 'FSH', value: '', date: '', trend: '', normalRange: '3–10 mIU/mL' },
    { name: 'AFC', value: '', date: '', trend: '', normalRange: '6–12' },
    { name: 'Estradiol', value: '', date: '', trend: '', normalRange: '30–100 pg/mL' },
  ];
  const clinic = fertility.clinic || { name: '', doctorName: '', doctorRating: '', notes: '' };

  const prenatalVisits = pregnancy.prenatalVisits || [];
  const testResults = pregnancy.testResults || [];
  const careTeam = pregnancy.careTeam || { ob: '', doula: '', hospital: '' };

  const cycleHistory = ttc.cycleHistory || [];
  const fertilityTests = ttc.fertilityTests || [];

  const children = postpartum.children || user?.children || [];
  const birthRecord = postpartum.birthRecord || {};
  const babyHealth = postpartum.babyHealth || {};

  const hormonePanel = menopause.hormonePanel || [];
  const hrt = menopause.hrt || {};
  const boneDensity = menopause.boneDensity || {};
  const cardiovascular = menopause.cardiovascular || {};

  const timeInChapter = journeyOverview.timeInChapter || timeline?.chapter_start_date
    ? `${Math.floor((Date.now() - new Date(timeline?.chapter_start_date || journeyOverview.chapterStartedDate || Date.now())) / 86400000)} days`
    : '—';
  const chapterStartedDate = journeyOverview.chapterStartedDate || timeline?.chapter_start_date || '—';
  const nextMilestone = journeyOverview.nextMilestone || '—';

  function saveOverview(patch) {
    updateHealthRecord({ journeyOverview: { ...journeyOverview, ...patch } });
  }
  function saveFertility(patch) {
    updateHealthRecord({ fertility: { ...fertility, ...patch } });
  }
  function savePregnancy(patch) {
    updateHealthRecord({ pregnancy: { ...pregnancy, ...patch } });
  }
  function saveTtc(patch) {
    updateHealthRecord({ ttc: { ...ttc, ...patch } });
  }
  function savePostpartum(patch) {
    updateHealthRecord({ postpartum: { ...postpartum, ...patch } });
  }
  function saveMenopause(patch) {
    updateHealthRecord({ menopause: { ...menopause, ...patch } });
  }

  return (
    <DashboardAppShell
      fontFamily={FONT_BODY}
      rightColumnGridTrack="220px"
      rightColumn={(
      <aside style={{ padding: 24, borderLeft: '1px solid var(--border)', background: 'white' }}>
        <h4 style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 12, textTransform: 'uppercase' }}>Quick links</h4>
        <Link to="/medications" style={{ display: 'block', fontSize: 14, color: 'var(--pink)', marginBottom: 8 }}>Medications →</Link>
        <Link to="/doctors" style={{ display: 'block', fontSize: 14, color: 'var(--pink)', marginBottom: 8 }}>My doctors →</Link>
        <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 16 }}>Export your full health record from each section using &quot;Share with my doctor.&quot;</p>
      </aside>
      )}
    >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontFamily: FONT_SERIF, fontSize: 24, color: 'var(--text)', marginBottom: 8 }}>Health record</h1>
            <span style={{ fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 999, background: chapterInfo?.bg || '#FBEAF0', color: chapterInfo?.color || '#993556' }}>
              {chapterLabel}
            </span>
          </div>
        </div>

        {/* JOURNEY OVERVIEW — all chapters */}
        <SectionCard
          title="Journey overview"
          onExport={() => exportSection('Journey_Overview', `OrivonAI Health Record — Journey Overview\n${new Date().toLocaleDateString()}\n\nChapter: ${chapterLabel}\nTime in chapter: ${timeInChapter}\nChapter started: ${chapterStartedDate}\nNext milestone: ${nextMilestone}`)}
        >
          {({ editing }) => (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, fontSize: 14 }}>
              <div>
                <div style={{ color: 'var(--muted)', marginBottom: 4 }}>Chapter</div>
                {editing ? <input value={journeyOverview.chapter ?? chapterLabel} onChange={(e) => saveOverview({ chapter: e.target.value })} style={inputStyle} /> : <div>{chapterLabel}</div>}
              </div>
              <div>
                <div style={{ color: 'var(--muted)', marginBottom: 4 }}>Time in chapter</div>
                {editing ? <input value={journeyOverview.timeInChapter ?? timeInChapter} onChange={(e) => saveOverview({ timeInChapter: e.target.value })} style={inputStyle} /> : <div>{timeInChapter}</div>}
              </div>
              <div>
                <div style={{ color: 'var(--muted)', marginBottom: 4 }}>Chapter started</div>
                {editing ? <input type="date" value={journeyOverview.chapterStartedDate ?? chapterStartedDate} onChange={(e) => saveOverview({ chapterStartedDate: e.target.value })} style={inputStyle} /> : <div>{String(chapterStartedDate).slice(0, 10)}</div>}
              </div>
              <div>
                <div style={{ color: 'var(--muted)', marginBottom: 4 }}>Next milestone</div>
                {editing ? <input value={journeyOverview.nextMilestone ?? nextMilestone} onChange={(e) => saveOverview({ nextMilestone: e.target.value })} style={inputStyle} /> : <div>{nextMilestone}</div>}
              </div>
            </div>
          )}
        </SectionCard>

        {/* FERTILITY / IVF */}
        {(chapter === 'fertility' || chapter === 'ivf') && (
          <>
            <SectionCard
              title="IVF cycles"
              onExport={() => exportSection('IVF_Cycles', `IVF Cycles\n\n${ivfCycles.map((c, i) => `Cycle ${i + 1}: ${c.startDate || '—'} | Protocol: ${c.protocol || '—'} | Eggs: ${c.eggsRetrieved ?? '—'} | Fertilized: ${c.fertilized ?? '—'} | Blastocysts: ${c.blastocysts ?? '—'} | Result: ${c.result || '—'}\nNotes: ${c.notes || ''}`).join('\n\n')}`)}
            >
              {({ editing }) => (
                <>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        <th style={{ textAlign: 'left', padding: 8 }}>Cycle #</th>
                        <th style={{ textAlign: 'left', padding: 8 }}>Start date</th>
                        <th style={{ textAlign: 'left', padding: 8 }}>Protocol</th>
                        <th style={{ textAlign: 'left', padding: 8 }}>Eggs</th>
                        <th style={{ textAlign: 'left', padding: 8 }}>Fertilized</th>
                        <th style={{ textAlign: 'left', padding: 8 }}>Blastocysts</th>
                        <th style={{ textAlign: 'left', padding: 8 }}>Result</th>
                        <th style={{ textAlign: 'left', padding: 8 }}>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ivfCycles.length === 0 ? (
                        <tr><td colSpan={8} style={{ padding: 16, color: 'var(--muted)' }}>No cycles yet. Add your first cycle.</td></tr>
                      ) : (
                        ivfCycles.map((c, i) => (
                          <tr key={c.id || i} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: 8 }}>{editing ? <input value={c.cycleNumber ?? i + 1} onChange={(e) => { const n = [...ivfCycles]; n[i] = { ...n[i], cycleNumber: e.target.value }; saveFertility({ ivfCycles: n }); }} style={{ ...inputStyle, width: 50 }} /> : (c.cycleNumber ?? i + 1)}</td>
                            <td style={{ padding: 8 }}>{editing ? <input type="date" value={c.startDate || ''} onChange={(e) => { const n = [...ivfCycles]; n[i] = { ...n[i], startDate: e.target.value }; saveFertility({ ivfCycles: n }); }} style={{ ...inputStyle, width: 120 }} /> : (c.startDate || '—')}</td>
                            <td style={{ padding: 8 }}>{editing ? <input value={c.protocol || ''} onChange={(e) => { const n = [...ivfCycles]; n[i] = { ...n[i], protocol: e.target.value }; saveFertility({ ivfCycles: n }); }} style={{ ...inputStyle, width: 100 }} /> : (c.protocol || '—')}</td>
                            <td style={{ padding: 8 }}>{editing ? <input value={c.eggsRetrieved ?? ''} onChange={(e) => { const n = [...ivfCycles]; n[i] = { ...n[i], eggsRetrieved: e.target.value }; saveFertility({ ivfCycles: n }); }} style={{ ...inputStyle, width: 50 }} /> : (c.eggsRetrieved ?? '—')}</td>
                            <td style={{ padding: 8 }}>{editing ? <input value={c.fertilized ?? ''} onChange={(e) => { const n = [...ivfCycles]; n[i] = { ...n[i], fertilized: e.target.value }; saveFertility({ ivfCycles: n }); }} style={{ ...inputStyle, width: 50 }} /> : (c.fertilized ?? '—')}</td>
                            <td style={{ padding: 8 }}>{editing ? <input value={c.blastocysts ?? ''} onChange={(e) => { const n = [...ivfCycles]; n[i] = { ...n[i], blastocysts: e.target.value }; saveFertility({ ivfCycles: n }); }} style={{ ...inputStyle, width: 50 }} /> : (c.blastocysts ?? '—')}</td>
                            <td style={{ padding: 8 }}>{editing ? <input value={c.result || ''} onChange={(e) => { const n = [...ivfCycles]; n[i] = { ...n[i], result: e.target.value }; saveFertility({ ivfCycles: n }); }} style={{ ...inputStyle, width: 80 }} /> : (c.result || '—')}</td>
                            <td style={{ padding: 8 }}>{editing ? <input value={c.notes || ''} onChange={(e) => { const n = [...ivfCycles]; n[i] = { ...n[i], notes: e.target.value }; saveFertility({ ivfCycles: n }); }} style={{ ...inputStyle, width: 120 }} /> : (c.notes || '—')}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  {editing && (
                    <button type="button" style={{ ...btnPrimary, marginTop: 12 }} onClick={() => {
                      const newCycle = { id: `c_${Date.now()}`, cycleNumber: ivfCycles.length + 1, startDate: new Date().toISOString().slice(0, 10), protocol: '', eggsRetrieved: '', fertilized: '', blastocysts: '', result: '', notes: '' };
                      saveFertility({ ivfCycles: [...ivfCycles, newCycle] });
                    }}>Add new cycle</button>
                  )}
                </>
              )}
            </SectionCard>

            <SectionCard title="Lab results" onExport={() => exportSection('Lab_Results', labResults.map(r => `${r.name}: ${r.value} (${r.date}) — ${r.normalRange}`).join('\n'))}>
              {({ editing }) => (
                <div style={{ display: 'grid', gap: 12 }}>
                  {labResults.map((r, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '100px 80px 100px 1fr 100px', gap: 12, alignItems: 'center' }}>
                      <span style={{ fontWeight: 600 }}>{r.name}</span>
                      {editing ? <input value={r.value} onChange={(e) => { const n = [...labResults]; n[i] = { ...n[i], value: e.target.value }; saveFertility({ labResults: n }); }} style={inputStyle} placeholder="Value" /> : <span>{r.value || '—'}</span>}
                      {editing ? <input type="date" value={r.date} onChange={(e) => { const n = [...labResults]; n[i] = { ...n[i], date: e.target.value }; saveFertility({ labResults: n }); }} style={inputStyle} /> : <span>{r.date || '—'}</span>}
                      <span style={{ color: 'var(--muted)', fontSize: 12 }}>{r.normalRange}</span>
                      <span>{r.trend === 'up' ? '↑' : r.trend === 'down' ? '↓' : '—'}</span>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>

            <SectionCard title="Current medications">
              {() => <p style={{ fontSize: 14 }}><Link to="/medications" style={{ color: 'var(--pink)' }}>View Medications page →</Link></p>}
            </SectionCard>

            <SectionCard title="Clinic & doctor" onExport={() => exportSection('Clinic_Doctor', `Clinic: ${clinic.name}\nDoctor: ${clinic.doctorName}\nRating: ${clinic.doctorRating}\nNotes: ${clinic.notes}`)}>
              {({ editing }) => (
                <div style={{ display: 'grid', gap: 12 }}>
                  <Row label="Clinic" value={clinic.name} editing={editing} onChange={(v) => saveFertility({ clinic: { ...clinic, name: v } })} />
                  <Row label="Doctor" value={clinic.doctorName} editing={editing} onChange={(v) => saveFertility({ clinic: { ...clinic, doctorName: v } })} />
                  <Row label="Doctor rating" value={clinic.doctorRating} editing={editing} onChange={(v) => saveFertility({ clinic: { ...clinic, doctorRating: v } })} />
                  <Row label="Notes" value={clinic.notes} editing={editing} onChange={(v) => saveFertility({ clinic: { ...clinic, notes: v } })} />
                </div>
              )}
            </SectionCard>
          </>
        )}

        {/* PREGNANCY */}
        {chapter === 'pregnancy' && (
          <>
            <SectionCard title="Pregnancy details" onExport={() => exportSection('Pregnancy_Details', `Due date: ${pregnancy.dueDate || '—'}\nGestational age: ${pregnancy.gestationalAge || '—'}\nConception method: ${pregnancy.conceptionMethod || '—'}`)}>
              {({ editing }) => (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                  <Row label="Due date" value={pregnancy.dueDate} editing={editing} type="date" onChange={(v) => savePregnancy({ dueDate: v })} />
                  <Row label="Gestational age" value={pregnancy.gestationalAge} editing={editing} onChange={(v) => savePregnancy({ gestationalAge: v })} />
                  <Row label="Conception method" value={pregnancy.conceptionMethod} editing={editing} onChange={(v) => savePregnancy({ conceptionMethod: v })} />
                </div>
              )}
            </SectionCard>

            <SectionCard title="Prenatal visits" onExport={() => exportSection('Prenatal_Visits', prenatalVisits.map(v => `${v.date} | ${v.provider} | ${v.findings} | Next: ${v.nextVisit}`).join('\n'))}>
              {({ editing }) => (
                <>
                  {prenatalVisits.length === 0 && !editing && <p style={{ color: 'var(--muted)' }}>No visits logged yet.</p>}
                  {prenatalVisits.map((v, i) => (
                    <div key={i} style={{ padding: 12, background: 'var(--sand)', borderRadius: 8, marginBottom: 8, fontSize: 13 }}>
                      {v.date} · {v.provider} · {v.findings} · Next: {v.nextVisit || '—'}
                    </div>
                  ))}
                  {editing && <button type="button" style={{ ...btnPrimary, marginTop: 8 }} onClick={() => savePregnancy({ prenatalVisits: [...prenatalVisits, { date: '', provider: '', findings: '', nextVisit: '' }] })}>Add visit</button>}
                </>
              )}
            </SectionCard>

            <SectionCard title="Test results" onExport={() => exportSection('Test_Results', testResults.map(t => `${t.name}: ${t.result} (${t.date})`).join('\n'))}>
              {({ editing }) => (
                <>
                  {testResults.length === 0 && !editing && <p style={{ color: 'var(--muted)' }}>No test results yet.</p>}
                  {testResults.map((t, i) => (
                    <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                      <input value={t.name} onChange={(e) => { const n = [...testResults]; n[i] = { ...n[i], name: e.target.value }; savePregnancy({ testResults: n }); }} style={inputStyle} placeholder="Test name" disabled={!editing} />
                      <input type="date" value={t.date} onChange={(e) => { const n = [...testResults]; n[i] = { ...n[i], date: e.target.value }; savePregnancy({ testResults: n }); }} style={inputStyle} disabled={!editing} />
                      <input value={t.result} onChange={(e) => { const n = [...testResults]; n[i] = { ...n[i], result: e.target.value }; savePregnancy({ testResults: n }); }} style={inputStyle} placeholder="Result" disabled={!editing} />
                    </div>
                  ))}
                  {editing && <button type="button" style={{ ...btnPrimary, marginTop: 8 }} onClick={() => savePregnancy({ testResults: [...testResults, { name: 'First trimester screen', date: '', result: '' }] })}>Add test</button>}
                </>
              )}
            </SectionCard>

            <SectionCard title="Birth plan" onExport={() => exportSection('Birth_Plan', pregnancy.birthPlan || '')}>
              {({ editing }) => (
                editing ? <textarea value={pregnancy.birthPlan || ''} onChange={(e) => savePregnancy({ birthPlan: e.target.value })} style={{ ...inputStyle, minHeight: 120 }} placeholder="Your birth preferences..." /> : <p style={{ whiteSpace: 'pre-wrap' }}>{pregnancy.birthPlan || 'Not yet added.'}</p>
              )}
            </SectionCard>

            <SectionCard title="Care team" onExport={() => exportSection('Care_Team', `OB/Midwife: ${careTeam.ob}\nDoula: ${careTeam.doula}\nHospital/Birth center: ${careTeam.hospital}`)}>
              {({ editing }) => (
                <div style={{ display: 'grid', gap: 12 }}>
                  <Row label="OB/Midwife" value={careTeam.ob} editing={editing} onChange={(v) => savePregnancy({ careTeam: { ...careTeam, ob: v } })} />
                  <Row label="Doula" value={careTeam.doula} editing={editing} onChange={(v) => savePregnancy({ careTeam: { ...careTeam, doula: v } })} />
                  <Row label="Hospital/Birth center" value={careTeam.hospital} editing={editing} onChange={(v) => savePregnancy({ careTeam: { ...careTeam, hospital: v } })} />
                </div>
              )}
            </SectionCard>
          </>
        )}

        {/* TTC */}
        {chapter === 'ttc' && (
          <>
            <SectionCard title="Cycle tracking summary" onExport={() => exportSection('Cycle_Summary', `Avg cycle length: ${ttc.cycleSummary?.avgCycleLength || '—'}\nLuteal phase: ${ttc.cycleSummary?.lutealPhase || '—'}\nOvulation day: ${ttc.cycleSummary?.ovulationDay || '—'}`)}>
              {({ editing }) => (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                  <Row label="Avg cycle length" value={ttc.cycleSummary?.avgCycleLength} editing={editing} onChange={(v) => saveTtc({ cycleSummary: { ...ttc.cycleSummary, avgCycleLength: v } })} />
                  <Row label="Luteal phase" value={ttc.cycleSummary?.lutealPhase} editing={editing} onChange={(v) => saveTtc({ cycleSummary: { ...ttc.cycleSummary, lutealPhase: v } })} />
                  <Row label="Ovulation day" value={ttc.cycleSummary?.ovulationDay} editing={editing} onChange={(v) => saveTtc({ cycleSummary: { ...ttc.cycleSummary, ovulationDay: v } })} />
                </div>
              )}
            </SectionCard>

            <SectionCard title="Cycle history" onExport={() => exportSection('Cycle_History', cycleHistory.map(c => `${c.dates || '—'} | ${c.observations || ''}`).join('\n'))}>
              {({ editing }) => (
                <>
                  {cycleHistory.length === 0 && !editing && <p style={{ color: 'var(--muted)' }}>No cycles logged yet.</p>}
                  {cycleHistory.map((c, i) => (
                    <div key={i} style={{ padding: 12, background: 'var(--sand)', borderRadius: 8, marginBottom: 8 }}>{c.dates} · {c.observations}</div>
                  ))}
                  {editing && <button type="button" style={{ ...btnPrimary, marginTop: 8 }} onClick={() => saveTtc({ cycleHistory: [...cycleHistory, { dates: '', observations: '' }] })}>Add cycle</button>}
                </>
              )}
            </SectionCard>

            <SectionCard title="Fertility tests" onExport={() => exportSection('Fertility_Tests', fertilityTests.map(t => `${t.name}: ${t.result} (${t.date})`).join('\n'))}>
              {({ editing }) => (
                <>
                  {fertilityTests.length === 0 && !editing && <p style={{ color: 'var(--muted)' }}>No tests yet.</p>}
                  {fertilityTests.map((t, i) => (
                    <div key={i} style={{ padding: 12, background: 'var(--sand)', borderRadius: 8, marginBottom: 8 }}>{t.name} · {t.result} · {t.date}</div>
                  ))}
                  {editing && <button type="button" style={{ ...btnPrimary, marginTop: 8 }} onClick={() => saveTtc({ fertilityTests: [...fertilityTests, { name: 'HSG', date: '', result: '' }] })}>Add test</button>}
                </>
              )}
            </SectionCard>
          </>
        )}

        {/* POSTPARTUM */}
        {chapter === 'postpartum' && (
          <>
            <SectionCard title="Birth record" onExport={() => exportSection('Birth_Record', `Birth date: ${birthRecord.birthDate || '—'}\nBirth type: ${birthRecord.birthType || '—'}\nWeight: ${birthRecord.weight || '—'}\nAPGAR: ${birthRecord.apgar || '—'}\nComplications: ${birthRecord.complications || '—'}`)}>
              {({ editing }) => (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                  <Row label="Birth date" value={birthRecord.birthDate} editing={editing} type="date" onChange={(v) => savePostpartum({ birthRecord: { ...birthRecord, birthDate: v } })} />
                  <Row label="Birth type" value={birthRecord.birthType} editing={editing} onChange={(v) => savePostpartum({ birthRecord: { ...birthRecord, birthType: v } })} />
                  <Row label="Weight" value={birthRecord.weight} editing={editing} onChange={(v) => savePostpartum({ birthRecord: { ...birthRecord, weight: v } })} />
                  <Row label="APGAR" value={birthRecord.apgar} editing={editing} onChange={(v) => savePostpartum({ birthRecord: { ...birthRecord, apgar: v } })} />
                  <Row label="Complications" value={birthRecord.complications} editing={editing} onChange={(v) => savePostpartum({ birthRecord: { ...birthRecord, complications: v } })} />
                </div>
              )}
            </SectionCard>

            <SectionCard title="Postpartum recovery">
              {({ editing }) => (
                <div style={{ display: 'grid', gap: 12 }}>
                  <Row label="6-week checkup date" value={postpartum.sixWeekCheckup?.date} editing={editing} type="date" onChange={(v) => savePostpartum({ sixWeekCheckup: { ...postpartum.sixWeekCheckup, date: v } })} />
                  <Row label="Notes" value={postpartum.sixWeekCheckup?.notes} editing={editing} onChange={(v) => savePostpartum({ sixWeekCheckup: { ...postpartum.sixWeekCheckup, notes: v } })} />
                </div>
              )}
            </SectionCard>

            <SectionCard title="Baby health" onExport={() => exportSection('Baby_Health', `Pediatrician: ${babyHealth.pediatrician || '—'}\nVaccination dates: ${babyHealth.vaccinationDates || '—'}\nMilestone log: ${babyHealth.milestoneLog || '—'}`)}>
              {({ editing }) => (
                <div style={{ display: 'grid', gap: 12 }}>
                  <Row label="Pediatrician" value={babyHealth.pediatrician} editing={editing} onChange={(v) => savePostpartum({ babyHealth: { ...babyHealth, pediatrician: v } })} />
                  <Row label="Vaccination dates" value={babyHealth.vaccinationDates} editing={editing} onChange={(v) => savePostpartum({ babyHealth: { ...babyHealth, vaccinationDates: v } })} />
                  <Row label="Milestone log" value={babyHealth.milestoneLog} editing={editing} onChange={(v) => savePostpartum({ babyHealth: { ...babyHealth, milestoneLog: v } })} />
                </div>
              )}
            </SectionCard>

            <SectionCard title="Children" onExport={() => exportSection('Children', children.map(c => `${c.name || 'Child'}: DOB ${c.dob || c.date_of_birth || '—'}`).join('\n'))}>
              {({ editing }) => (
                <>
                  {children.length === 0 && !editing && <p style={{ color: 'var(--muted)' }}>No children added yet.</p>}
                  {children.map((c, i) => (
                    <div key={i} style={{ padding: 12, background: 'var(--sand)', borderRadius: 8, marginBottom: 8 }}>
                      {c.name || 'Child'} · DOB: {c.dob || c.date_of_birth || c.birth_year || '—'}
                    </div>
                  ))}
                </>
              )}
            </SectionCard>
          </>
        )}

        {/* MENOPAUSE */}
        {chapter === 'menopause' && (
          <>
            <SectionCard title="Symptom history" onExport={() => exportSection('Symptom_History', (menopause.symptomHistory || []).map(s => `${s.date}: ${s.symptoms}`).join('\n'))}>
              {() => <p style={{ color: 'var(--muted)' }}>Chart/log of symptoms over time — coming soon.</p>}
            </SectionCard>

            <SectionCard title="Hormone panel results" onExport={() => exportSection('Hormone_Panel', hormonePanel.map(h => `${h.name}: ${h.value} (${h.date})`).join('\n'))}>
              {({ editing }) => (
                <>
                  {hormonePanel.length === 0 && !editing && <p style={{ color: 'var(--muted)' }}>No results yet.</p>}
                  {hormonePanel.map((h, i) => (
                    <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                      <input value={h.name} onChange={(e) => { const n = [...hormonePanel]; n[i] = { ...n[i], name: e.target.value }; saveMenopause({ hormonePanel: n }); }} style={inputStyle} placeholder="FSH, LH, Estradiol" disabled={!editing} />
                      <input value={h.value} onChange={(e) => { const n = [...hormonePanel]; n[i] = { ...n[i], value: e.target.value }; saveMenopause({ hormonePanel: n }); }} style={inputStyle} disabled={!editing} />
                      <input type="date" value={h.date} onChange={(e) => { const n = [...hormonePanel]; n[i] = { ...n[i], date: e.target.value }; saveMenopause({ hormonePanel: n }); }} style={inputStyle} disabled={!editing} />
                    </div>
                  ))}
                  {editing && <button type="button" style={{ ...btnPrimary, marginTop: 8 }} onClick={() => saveMenopause({ hormonePanel: [...hormonePanel, { name: 'FSH', value: '', date: '' }] })}>Add result</button>}
                </>
              )}
            </SectionCard>

            <SectionCard title="HRT details" onExport={() => exportSection('HRT', `Type: ${hrt.type || '—'}\nDose: ${hrt.dose || '—'}\nStart date: ${hrt.startDate || '—'}\nPrescribing doctor: ${hrt.doctor || '—'}`)}>
              {({ editing }) => (
                <div style={{ display: 'grid', gap: 12 }}>
                  <Row label="Type" value={hrt.type} editing={editing} onChange={(v) => saveMenopause({ hrt: { ...hrt, type: v } })} />
                  <Row label="Dose" value={hrt.dose} editing={editing} onChange={(v) => saveMenopause({ hrt: { ...hrt, dose: v } })} />
                  <Row label="Start date" value={hrt.startDate} editing={editing} type="date" onChange={(v) => saveMenopause({ hrt: { ...hrt, startDate: v } })} />
                  <Row label="Prescribing doctor" value={hrt.doctor} editing={editing} onChange={(v) => saveMenopause({ hrt: { ...hrt, doctor: v } })} />
                </div>
              )}
            </SectionCard>

            <SectionCard title="Bone density" onExport={() => exportSection('Bone_Density', `Last DEXA: ${boneDensity.date || '—'}\nResult: ${boneDensity.result || '—'}`)}>
              {({ editing }) => (
                <div style={{ display: 'grid', gap: 12 }}>
                  <Row label="Last DEXA date" value={boneDensity.date} editing={editing} type="date" onChange={(v) => saveMenopause({ boneDensity: { ...boneDensity, date: v } })} />
                  <Row label="Result" value={boneDensity.result} editing={editing} onChange={(v) => saveMenopause({ boneDensity: { ...boneDensity, result: v } })} />
                </div>
              )}
            </SectionCard>

            <SectionCard title="Cardiovascular" onExport={() => exportSection('Cardiovascular', `Last cholesterol panel: ${cardiovascular.cholesterolPanel || '—'}\nBlood pressure trend: ${cardiovascular.bloodPressureTrend || '—'}`)}>
              {({ editing }) => (
                <div style={{ display: 'grid', gap: 12 }}>
                  <Row label="Last cholesterol panel" value={cardiovascular.cholesterolPanel} editing={editing} onChange={(v) => saveMenopause({ cardiovascular: { ...cardiovascular, cholesterolPanel: v } })} />
                  <Row label="Blood pressure trend" value={cardiovascular.bloodPressureTrend} editing={editing} onChange={(v) => saveMenopause({ cardiovascular: { ...cardiovascular, bloodPressureTrend: v } })} />
                </div>
              )}
            </SectionCard>
          </>
        )}

        {/* LOSS HISTORY */}
        {hasLoss && (
          <SectionCard title="🔒 Loss history" onExport={null}>
            {() => (
              <div style={{ background: '#FBEAF0', border: '1px solid #F4C0D1', borderRadius: 12, padding: 16, marginBottom: 12 }}>
                <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>This section is private and encrypted. Your loss history is stored separately and never shared.</p>
                {lossHistory.length === 0 ? (
                  <p style={{ color: 'var(--muted)' }}>No losses recorded.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {lossHistory.map((entry, i) => (
                      <div key={i} style={{ padding: 12, background: 'white', borderRadius: 8, border: '1px solid var(--border)' }}>
                        <div style={{ fontSize: 13 }}>
                          <strong>{LOSS_TYPES.includes(entry.loss_type) ? entry.loss_type.replace(/_/g, ' ') : entry.loss_type}</strong> · {entry.loss_date}
                          {entry.gestational_age_weeks != null && ` · ${entry.gestational_age_weeks}w`}
                        </div>
                        {entry.due_date && <div style={{ fontSize: 12, color: 'var(--muted)' }}>Due date: {entry.due_date}</div>}
                        {entry.notes && <div style={{ fontSize: 12 }}>{entry.notes}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </SectionCard>
        )}

        <div style={{ marginTop: 24, fontSize: 13, color: 'var(--muted)' }}>
          All changes are saved automatically to your health record.
        </div>
    </DashboardAppShell>
  );
}

function Row({ label, value, editing, onChange, type }) {
  const inputStyle = { padding: 8, borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, width: '100%' };
  return (
    <div>
      <div style={{ color: 'var(--muted)', marginBottom: 4, fontSize: 12 }}>{label}</div>
      {editing ? <input type={type || 'text'} value={value || ''} onChange={(e) => onChange(e.target.value)} style={inputStyle} /> : <div>{value || '—'}</div>}
    </div>
  );
}

const inputStyle = { padding: 8, borderRadius: 8, border: '1px solid var(--border)', fontSize: 14 };
